import { FocusEvent, useMemo, useCallback } from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import distributionApi from 'services/api/distribution';
import { actions } from 'services/store/distribution';
import { AdvancedReleaseSongFieldType } from 'services/store/distribution/types';
import { selectReleaseSongAdvancedOptions } from 'services/store/distribution/selectors';
import LoadingAdornment from 'pages/Distribution/components/LoadingAdornment';

type PropsType = {
  releaseId: string;
  field: AdvancedReleaseSongFieldType;
};

const AdvancedOptionsTextfield = ({ releaseId, field }: PropsType) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const advancedOptions = useSelector(
    selectReleaseSongAdvancedOptions(releaseId, field)
  );
  const { data, loading, error } = advancedOptions;
  const [updateReleaseSong] = distributionApi.useUpdateReleaseSongMutation();
  const { setReleaseSongFieldError } = actions;

  const currentValue = useMemo(() => data?.join(', ') || '', [data]);

  const convertToStringArray = (strVal: string) =>
    strVal.split(',').map((str) => str.trim());

  const handleChange = useCallback(() => {
    if (!error) {
      return;
    }
    dispatch(setReleaseSongFieldError({ releaseId, field, error: null }));
  }, [releaseId, field, error, setReleaseSongFieldError, dispatch]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if ((!currentValue && !value) || currentValue === value) {
        return;
      }
      updateReleaseSong({
        releaseId,
        field,
        value: convertToStringArray(value),
      });
    },
    [currentValue, field, releaseId, updateReleaseSong]
  );

  return (
    <TextField
      fullWidth
      disabled={loading}
      label={t(`distribution:tracklist:fields:${field}:placeholder`)}
      defaultValue={currentValue}
      error={!!error}
      helperText={error && error.message}
      InputProps={{
        ...(loading && {
          endAdornment: <LoadingAdornment />,
        }),
      }}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default AdvancedOptionsTextfield;
